import React from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, ShieldCheck, LineChart } from "lucide-react";

const Login = () => {
  const { user } = useAuth();

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (user) {
    window.location.href = "/dashboard";
    return null;
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#0F172A] grain">
      {/* Left: brand & hero */}
      <div className="relative flex flex-col justify-between p-10 lg:p-14 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#101a2c] to-[#0B1120]" />
        <div
          className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, #C9A24B 0%, transparent 70%)" }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#C9A24B] flex items-center justify-center gold-glow">
              <Sparkles size={22} className="text-[#0F172A]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="overline">Youngness Centre</div>
              <div className="font-heading text-xl text-white">by Dr. Awish</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-xl">
          <div className="overline mb-4">Reception & Patient Experience CRM</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium tracking-tight text-white leading-tight">
            Every warm welcome,<br />measured with{" "}
            <span className="text-[#C9A24B]">precision</span>.
          </h1>
          <p className="mt-6 text-slate-400 font-body leading-relaxed max-w-md">
            A luxury reception command centre for Sarita Vihar, Mathura and Jaipur — daily activity logs,
            profile-aware KPIs, complaint funnel, dues tracker and an AI-assisted intelligence panel.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            <Feature icon={<ShieldCheck size={18} />} label="Role-based access" />
            <Feature icon={<LineChart size={18} />} label="Weighted scoring" />
            <Feature icon={<Sparkles size={18} />} label="AI insights" />
          </div>
        </div>
        <div className="relative z-10 text-slate-500 text-xs font-mono">
          © {new Date().getFullYear()} Youngness Centre — Confidential Reception Ops
        </div>
      </div>

      {/* Right: sign-in card */}
      <div className="flex items-center justify-center p-8 lg:p-14 bg-[#0B1120]">
        <div className="w-full max-w-md">
          <div className="bg-[#1E293B] border border-[#334155] rounded-2xl p-10 gold-glow">
            <div className="overline mb-2">Secure Access</div>
            <h2 className="font-heading text-3xl text-white mb-2">Welcome back.</h2>
            <p className="text-slate-400 font-body mb-8">
              Sign in with your Google account. Your role and dashboard are determined by the Reception Master List.
            </p>
            <Button
              onClick={handleLogin}
              data-testid="google-signin-btn"
              className="w-full h-12 bg-white hover:bg-slate-100 text-slate-900 font-medium rounded-lg flex items-center justify-center gap-3 transition-colors"
            >
              <GoogleIcon />
              <span>Continue with Google</span>
            </Button>
            <div className="mt-8 pt-6 border-t border-[#334155]">
              <div className="overline mb-3">What you get after signing in</div>
              <ul className="space-y-2 text-sm text-slate-400 font-body">
                <li>• Executive Dashboard with 20 Reception Objectives</li>
                <li>• Profile-aware Daily Reception Log</li>
                <li>• Weighted Performance Scorecard (per Employee ID)</li>
                <li>• Complaint Funnel & Due Patient Tracker</li>
                <li>• AI Assistant (Claude Sonnet 4.5)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Feature = ({ icon, label }) => (
  <div className="flex flex-col items-start gap-2">
    <div className="w-9 h-9 rounded-lg bg-[#1E293B] border border-[#334155] flex items-center justify-center text-[#C9A24B]">
      {icon}
    </div>
    <div className="text-xs font-mono uppercase tracking-widest text-slate-400">{label}</div>
  </div>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

export default Login;
